import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import QuotaDisplay from '../QuotaDisplay';
import { ArrowLeft, Settings, BarChart3 } from 'lucide-react';
import './QuotasPage.css';

interface QuotasPageProps {
  onClose: () => void;
}

const QuotasPage: React.FC<QuotasPageProps> = ({ onClose }) => {
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="quotas-page">
        <div className="quotas-page-content">
          <div className="quotas-page-header">
            <button onClick={onClose} className="quotas-page-back-btn">
              <ArrowLeft size={20} />
              Back
            </button>
            <h1>Quota Usage</h1>
          </div>
          <div className="quotas-page-body">
            <p>Please sign in to view your quota usage.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="quotas-page">
      <div className="quotas-page-content">
        <div className="quotas-page-header">
          <button onClick={onClose} className="quotas-page-back-btn">
            <ArrowLeft size={20} />
            Back
          </button>
          <h1>Quota Usage</h1>
          <div className="quotas-page-actions">
            <a href="/pricing" className="quotas-page-action-btn">
              <BarChart3 size={16} />
              View Plans
            </a>
          </div>
        </div>
        
        <div className="quotas-page-body">
          <div className="quotas-page-intro">
            <h2>Your Usage Overview</h2>
            <p>
              Track your usage across all ThreadIt features. Upgrade your plan to unlock higher limits and unlimited access.
            </p>
          </div>
          
          <div className="quotas-page-main">
            <QuotaDisplay variant="detailed" showUpgradePrompt={true} />
          </div>
          
          <div className="quotas-page-info">
            <div className="quotas-info-section">
              <h3>How Quotas Work</h3>
              <div className="quotas-info-grid">
                <div className="quotas-info-item">
                  <div className="quotas-info-icon">💬</div>
                  <div className="quotas-info-content">
                    <h4>Monthly Chat Messages</h4>
                    <p>Total number of chat messages you can send per month across all notes.</p>
                  </div>
                </div>
                
                <div className="quotas-info-item">
                  <div className="quotas-info-icon">📄</div>
                  <div className="quotas-info-content">
                    <h4>Total Canvases</h4>
                    <p>Total number of canvases you can create and save.</p>
                  </div>
                </div>
                
                <div className="quotas-info-item">
                  <div className="quotas-info-icon">💡</div>
                  <div className="quotas-info-content">
                    <h4>Thread Starters</h4>
                    <p>Main idea notes you can create across all canvases.</p>
                  </div>
                </div>
                
                <div className="quotas-info-item">
                  <div className="quotas-info-icon">🌿</div>
                  <div className="quotas-info-content">
                    <h4>Total Threads</h4>
                    <p>All notes you can create across all canvases.</p>
                  </div>
                </div>
                
                <div className="quotas-info-item">
                  <div className="quotas-info-icon">📎</div>
                  <div className="quotas-info-content">
                    <h4>File Attachments</h4>
                    <p>Files you can upload across all canvases.</p>
                  </div>
                </div>
                
                <div className="quotas-info-item">
                  <div className="quotas-info-icon">🔍</div>
                  <div className="quotas-info-content">
                    <h4>Web Searches</h4>
                    <p>AI web search queries you can perform.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="quotas-info-section">
              <h3>Quota Reset</h3>
              <p>
                Free and Basic plan quotas reset monthly. Pro plan users have unlimited access to all features.
                Your current usage will reset on your next billing cycle.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotasPage;
